import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import {
  renderTooltipOnBottom,
  renderTooltipOnLeft,
  renderFormError
} from '../../../shared/form-controls/jui-form-control.utils';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';

@Component({
  tag: 'jui-textarea',
  styleUrl: 'jui-textarea.scss',
  shadow: true
})
export class JuiTextarea implements ComponentInterface {
  @Element() el: HTMLElement;
  /**
   * The textarea label.
   */
  @Prop() label: string;

  /**
   * The form name for the textarea control.
   */
  @Prop() name = '';

  /**
   * The textarea placeholder. Defaults to the label.
   */
  @Prop() placeholder = '';

  /**
   * The textarea value.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true })
  value: string;

  /**
   * The errors for the textarea
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the textarea
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Marking the textarea field as readonly. Defaults to false
   */
  @Prop({ attribute: 'readonly', reflect: true })
  readonly = false;

  /**
   * Marking the textarea field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Marking the textarea field as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Set the max length of the textarea
   */
  @Prop() maxlength?: number | null = null;

  /**
   * Set the min length of the input
   */
  @Prop() minlength?: number | null = null;

  /**
   * Optionally autofocus the textarea. Defaults to false
   */
  @Prop() autofocus = false;

  /**
   * Set the textarea field to be transitionable
   */
  @Prop() transitional = false;

  /**
   * Set the textarea field to be resizable
   */
  @Prop() resize = false;

  /**
   * Set the textarea rows
   */
  @Prop({ attribute: 'rows', reflect: true }) rows = 5;

  /**
   * Set the size of the textarea. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Output event emitted when change is fired
   */
  @Event() public juiChange: EventEmitter<string>;

  /**
   * Output event emitted when input is fired
   */
  @Event() public juiInput: EventEmitter<string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiBlur: EventEmitter<string>;

  /**
   * Output event emitted when focus is fired
   */
  @Event() public juiFocus: EventEmitter<string>;

  @State()
  private errorMessages = null;

  @State()
  private inputLength = 0;

  @State()
  private touched = false;

  @State()
  private focused = false;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  componentDidLoad(): void {
    if (this.autofocus) {
      this.focusControl();
    }
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  componentWillLoad(): void {
    if (this.value) {
      this.inputLength = this.value.length;
    }

    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  componentWillUpdate(): void {
    this.renderError();
  }

  private onInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.inputLength = this.value.length;
    this.juiInput.emit(this.value);
  }

  private onChange(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.juiChange.emit(this.value);
  }

  private onBlur(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.focused = false;
    this.juiBlur.emit(this.value);
  }

  private onFocus(event: Event): void {
    this.touched = true;
    this.focused = true;
    this.value = (event.target as HTMLInputElement).value;
    this.juiFocus.emit(this.value);
  }

  private focusControl(): void {
    const inputEl = this.el.shadowRoot.querySelector('.jui-control__input') as HTMLInputElement;
    inputEl.focus();
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;

    const helpText = this.help && !this.transitional ? <div class="jui-control__help">{this.help}</div> : null;

    const characterCount = this.maxlength ? (
      <div>
        {this.inputLength}/{this.maxlength}
      </div>
    ) : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div
          class={{
            'jui-control': true,
            [`jui-control--${this.size}`]: true,
            'jui-control--transitional': !!this.transitional,
            'jui-control--resize': !!this.resize
          }}
        >
          {this.label ? (
            <label
              {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
              class={{
                'jui-control__label': true,
                'jui-control__label--has-value': !!this.value,
                'jui-control__label--has-focus': !!this.focused,
                'jui-control__label--disabled': !!this.disable,
                'jui-control__label--narrower': !!this.tooltip
              }}
            >
              <div
                class={{
                  'jui-control__label-text': true
                }}
              >
                {this.required ? `${this.label} *` : this.label}
                {characterCount}
              </div>
            </label>
          ) : null}
          {helpText}
          {tooltip}
          <textarea
            {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
            class={{
              'jui-control__input': true,
              'jui-control__input--has-value': !!this.value,
              'jui-control__input--invalid': (!!this.errors && this.touched) || (!!this.errors && !!this.focused)
            }}
            name={this.name}
            placeholder={this.placeholder.length ? this.placeholder : this.label}
            maxLength={!isNaN(this.maxlength) ? this.maxlength : undefined}
            minLength={!isNaN(this.minlength) ? this.minlength : undefined}
            required={this.required}
            disabled={this.disable}
            readOnly={this.readonly}
            rows={this.rows}
            onInput={this.onInput.bind(this)}
            onChange={this.onChange.bind(this)}
            onFocusout={this.onBlur.bind(this)}
            onFocusin={this.onFocus.bind(this)}
            value={this.value}
          >
            {this.value}
          </textarea>
          {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el);
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el);
    }
  }

  private renderError(): void {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
